import "./src/styles/global.css";
import i18n, { loadLanguage, registerLanguage } from "./src/i18n";
import {
  trackPageView,
  trackOutboundClick,
  trackFileDownload,
  trackClickToCall,
  trackMailtoClick,
  trackScroll75,
  trackCtaClick,
} from "./src/lib/analytics";
import { captureAttribution, clearAttribution } from "./src/lib/attribution";
import { subscribeConsent } from "./src/lib/consent";
import { TRACKING_ENABLED, isTrackingAllowedOnCurrentHost } from "./src/config/tracking";

let globalHandlersAttached = false;
let scroll75Fired = false;
let unsubscribeConsent;
let routeUpdateVersion = 0;
// Il consenso arriva da CookieScript, che viene caricato da GTM: alla prima
// route non e' ancora noto e il pageview viene scartato. Lo teniamo qui per
// rispedirlo quando l'utente accetta, senza contarlo due volte.
let pendingPageView = null;

function isInternalHost(host) {
  if (!host) return true;
  const h = host.toLowerCase();
  const here = window.location.hostname.toLowerCase();
  return h === here || h.endsWith("." + here);
}

function getFileExtension(url) {
  try {
    const u = new URL(url, window.location.href);
    const m = u.pathname.match(/\.([a-z0-9]{2,5})(?:$|\?)/i);
    return m ? m[1].toLowerCase() : "";
  } catch {
    return "";
  }
}

const DOWNLOAD_EXTS = new Set([
  "pdf",
  "doc",
  "docx",
  "xls",
  "xlsx",
  "csv",
  "zip",
  "ppt",
  "pptx",
  "txt",
]);

function handleDocumentClick(e) {
  const link = e.target.closest && e.target.closest("a[href]");
  if (!link) return;
  const href = link.getAttribute("href");
  if (!href || href.startsWith("javascript:")) return;
  const linkText = (link.innerText || link.textContent || "").trim().slice(0, 120);
  // CtaButton gestisce gia' il proprio evento. Questo fallback copre le CTA
  // storiche costruite come Link con le classi condivise btn-*.
  const isLegacyCta =
    link.dataset.analyticsCta !== "true" &&
    Boolean(link.dataset.ctaId) &&
    ["btn-primary", "btn-outline-ink", "btn-outline-cream"].some((cls) =>
      link.classList.contains(cls)
    );
  if (isLegacyCta) {
    const section = link.closest("section");
    trackCtaClick({
      ctaId: link.dataset.ctaId,
      ctaLabel: linkText,
      ctaLocation: link.dataset.ctaLocation || section?.id || "page",
      ctaDestination: href,
    });
  }
  if (href.startsWith("#")) return;
  if (href.startsWith("mailto:")) {
    trackMailtoClick({ linkText });
    return;
  }
  if (href.startsWith("tel:")) {
    trackClickToCall({ linkText });
    return;
  }
  let url;
  try {
    url = new URL(href, window.location.href);
  } catch {
    return;
  }
  const ext = getFileExtension(url.href);
  if (ext && DOWNLOAD_EXTS.has(ext)) {
    trackFileDownload({ url: url.href, fileExtension: ext, linkText });
    return;
  }
  if (!isInternalHost(url.hostname)) {
    trackOutboundClick({ url: url.href, kind: "outbound", linkText });
  }
}

function onScroll() {
  if (scroll75Fired) return;
  const doc = document.documentElement;
  const scrollTop = window.scrollY || doc.scrollTop || 0;
  const viewportH = window.innerHeight || doc.clientHeight || 0;
  const reachable = Math.max((doc.scrollHeight || 0) - viewportH, 1);
  if ((scrollTop / reachable) * 100 >= 75) {
    scroll75Fired = true;
    trackScroll75();
  }
}

function attachGlobalHandlers() {
  if (globalHandlersAttached) return;
  globalHandlersAttached = true;
  document.addEventListener("click", handleDocumentClick, { capture: true });
  let scrollTicking = false;
  window.addEventListener(
    "scroll",
    () => {
      if (scrollTicking) return;
      scrollTicking = true;
      window.requestAnimationFrame(() => {
        onScroll();
        scrollTicking = false;
      });
    },
    { passive: true }
  );
}

export const onClientEntry = async () => {
  if (typeof window === "undefined") return;
  if (!unsubscribeConsent) {
    unsubscribeConsent = subscribeConsent((state) => {
      if (state.known && !state.analytics && !state.marketing) {
        // Consent revocation must remove both disk and in-memory attribution.
        clearAttribution();
      } else if (state.known) {
        // Capture a campaign that arrived before CookieScript finished loading.
        captureAttribution();
        if (pendingPageView && trackPageView(pendingPageView)) pendingPageView = null;
      }
    });
  }
  captureAttribution();
  if (TRACKING_ENABLED && isTrackingAllowedOnCurrentHost()) attachGlobalHandlers();
  // Le stringhe della lingua del path arrivano in un chunk separato: Gatsby
  // attende questa promise prima di idratare (production-app.js chiama
  // apiRunnerAsync("onClientEntry").then(render)), altrimenti il primo render
  // troverebbe le risorse vuote e stamperebbe le chiavi.
  const lng = window.location.pathname.startsWith("/it/") ? "it" : "en";
  await loadLanguage(lng);
  if (i18n.language !== lng) {
    i18n.changeLanguage(lng);
  }
};

export const onRouteUpdate = ({ location, prevLocation }) => {
  if (typeof window === "undefined") return;
  const updateVersion = ++routeUpdateVersion;
  captureAttribution();
  const path = (location?.pathname || "/") + (location?.search || "");
  const virtualReferrer = prevLocation
    ? (prevLocation.pathname || "/") + (prevLocation.search || "")
    : document.referrer;
  const send = () => {
    if (updateVersion !== routeUpdateVersion) return;
    const pageView = { path, title: document.title, referrer: virtualReferrer };
    pendingPageView = trackPageView(pageView) ? null : pageView;
  };
  if (prevLocation) {
    window.setTimeout(send, 50);
  } else {
    send();
  }
  scroll75Fired = false;
};

export const wrapPageElement = ({ element, props }) => {
  const pathname = props.location?.pathname || "/";
  const langFromPath = pathname.startsWith("/it/") ? "it" : "en";
  // Le stringhe della pagina arrivano nel pageContext, cioe' in
  // page-data.json, che il loader scarica prima di renderizzare la route (vedi
  // onCreatePage in gatsby-node.js). Registrarle qui, prima di restituire
  // l'element, garantisce che il primo render trovi il testo e non le chiavi.
  if (props.pageContext?.i18nResources) {
    registerLanguage(langFromPath, props.pageContext.i18nResources);
  }
  if (i18n.language !== langFromPath) {
    i18n.changeLanguage(langFromPath);
  }
  return element;
};

export const shouldUpdateScroll = ({ routerProps: { location }, prevRouterProps }) => {
  if (location.hash) {
    const prefersReducedMotion =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const behavior = prefersReducedMotion ? "auto" : "smooth";
    const scrollToHash = () => {
      const el = document.getElementById(location.hash.slice(1));
      if (el) {
        el.scrollIntoView({ behavior, block: "start" });
        return true;
      }
      return false;
    };
    const samePath = prevRouterProps?.location?.pathname === location.pathname;
    const delay = samePath ? 0 : 80;
    window.setTimeout(() => {
      if (!scrollToHash()) {
        window.setTimeout(scrollToHash, 200);
      }
    }, delay);
    return false;
  }
  return true;
};
